import { KaboodleToaster } from "components/common";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  usePatchReportPrescriptionAllocationByIdMutation,
  usePatchReportTreatmentAllocationByIdMutation,
} from "services/reports-api";
import type { MSADecisionLineItemType } from "types/DataTypes";
import { useDebouncedCallback } from "use-debounce";

import { getAllocationValues } from "./CMSDecisionModalForm";

export type FormState = {
  unit_price: string | number;
  units_per_year: string | number | null;
  annual_cost: string | number | null;
  number_of_years: string | number;
  total_cost: string | number;
  units_per_month: string | number | null;
  dosage: string | number | null;
  proposed_unit_price: string | number;
  proposed_units_per_year: string | number | null;
  proposed_annual_cost: string | number | null;
  proposed_number_of_years: string | number;
  proposed_total_cost: string | number;
  proposed_units_per_month: string | number | null;
  proposed_dosage: string | number | null;
};

export const useCMSDecisionModal = (
  isOpen: boolean,
  decisionItem: MSADecisionLineItemType,
) => {
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [lastItemId, setLastItemId] = useState<string | null>(null);
  const [savingFields, setSavingFields] = useState<{ [key: string]: boolean }>(
    {},
  );
  // track which fields have pending saves
  const pendingSaves = useRef<Set<string>>(new Set());

  const formMethods = useForm<FormState>();
  const {
    control,
    reset,
    setValue,
    formState: { dirtyFields },
  } = formMethods;

  const itemId = decisionItem?.id;
  const isMedical = !!decisionItem?.decision_treatment_allocation;

  // get the allocation ID for saving
  const allocationId = isMedical
    ? decisionItem?.decision_treatment_allocation?.id
    : decisionItem?.decision_prescription_allocation?.id;

  const [patchReportPrescriptionAllocationById] =
    usePatchReportPrescriptionAllocationByIdMutation();
  const [patchReportTreatmentAllocationById] =
    usePatchReportTreatmentAllocationByIdMutation();

  // memoize the current allocation data to prevent unnecessary recalculations
  const currentAllocationData = useMemo(() => {
    if (!decisionItem) return null;
    return isMedical
      ? decisionItem.decision_treatment_allocation
      : decisionItem.decision_prescription_allocation;
  }, [decisionItem?.id, isMedical]);

  // memoize proposed values to prevent unnecessary recalculations
  const proposedValues = useMemo(
    () => getAllocationValues(decisionItem, "proposed"),
    [decisionItem?.id],
  );

  // update form values when allocation data changes
  const updateFormValues = useCallback(
    (
      currentData: {
        unit_price: string | number;
        units_per_year: string | number | null;
        annual_cost: string | number | null;
        number_of_years: string | number;
        total_cost: string | number;
        units_per_month: string | number | null;
        dosage: string | number | null;
      },
      keepDirty = true,
    ) => {
      if (!currentData) return;

      // helper to set value only if field is not dirty
      const setValueIfNotDirty = (
        name: keyof FormState,
        value: FormState[keyof FormState],
      ) => {
        if (!keepDirty || !dirtyFields[name]) {
          setValue(name, value);
        }
      };

      // update current values
      setValueIfNotDirty("unit_price", currentData.unit_price);
      setValueIfNotDirty("units_per_year", currentData.units_per_year);
      setValueIfNotDirty("annual_cost", currentData.annual_cost);
      setValueIfNotDirty("number_of_years", currentData.number_of_years);
      setValueIfNotDirty("total_cost", currentData.total_cost);
      setValueIfNotDirty("units_per_month", currentData.units_per_month);
      setValueIfNotDirty("dosage", currentData.dosage);

      // always update proposed values since they're read-only
      setValue("proposed_unit_price", proposedValues.unit_price);
      setValue("proposed_units_per_year", proposedValues.units_per_year);
      setValue("proposed_annual_cost", proposedValues.annual_cost);
      setValue("proposed_number_of_years", proposedValues.number_of_years);
      setValue("proposed_total_cost", proposedValues.total_cost);
      setValue("proposed_units_per_month", proposedValues.units_per_month);
      setValue("proposed_dosage", proposedValues.dosage);
    },
    [setValue, dirtyFields, proposedValues],
  );

  const debouncedSave = useDebouncedCallback(
    async (id: number, field: string, value: string | number) => {
      if (!id) {
        setSavingFields((prev) => ({ ...prev, [field]: false }));
        pendingSaves.current.delete(field);
        return;
      }

      try {
        let updatedData;
        if (!isMedical) {
          updatedData = await patchReportPrescriptionAllocationById({
            id: id.toString(),
            data: { [field]: value },
          }).unwrap();
        } else {
          updatedData = await patchReportTreatmentAllocationById({
            id: id.toString(),
            data: { [field]: value },
          }).unwrap();
        }

        // immediately update form with new values after successful patch
        if (updatedData) {
          updateFormValues(updatedData);
        }
      } catch (error) {
        KaboodleToaster.error(`Failed to update ${field}`);
      } finally {
        pendingSaves.current.delete(field);
        // only clear saving state if there are no pending saves for this field
        if (!pendingSaves.current.has(field)) {
          setSavingFields((prev) => ({ ...prev, [field]: false }));
        }
      }
    },
    300,
  );

  const handleSave = useCallback(
    (id: number, field: string, value: string | number) => {
      if (!allocationId) return;

      pendingSaves.current.add(field);
      setSavingFields((prev) => ({ ...prev, [field]: true }));

      debouncedSave.cancel();

      return debouncedSave(allocationId, field, value);
    },
    [debouncedSave, allocationId],
  );

  // cleanup effect when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset({});
      setLastItemId(null);
      setIsInitialLoad(true);
      setSavingFields({});
      debouncedSave.cancel();
      pendingSaves.current.clear();
    }
  }, [isOpen, reset, debouncedSave]);

  const isAnySaving = useMemo(() => {
    return (
      Object.values(savingFields).some(Boolean) && pendingSaves.current.size > 0
    );
  }, [savingFields]);

  // handle form data updates
  useEffect(() => {
    if (!isOpen || !itemId || !currentAllocationData) return;

    const currentItemId = itemId.toString();
    const isNewItem = currentItemId !== lastItemId;

    if (isNewItem) {
      setLastItemId(currentItemId);
      setIsInitialLoad(true);
      reset({});
      return;
    }

    // don't keep dirty values on initial load
    updateFormValues(currentAllocationData, !isInitialLoad);
    setIsInitialLoad(false);
  }, [
    isOpen,
    itemId,
    lastItemId,
    currentAllocationData,
    updateFormValues,
    reset,
  ]);

  return {
    control,
    formMethods,
    handleSave,
    isMedical,
    savingFields,
    proposedValues,
    isAnySaving,
  };
};
